import { AiErrorBoundary } from "./components/AiErrorBoundary";
import React, { Component, ErrorInfo, ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, RequireAuth, useAuth } from "@/contexts/AuthContext";
import JsonLdSchema from "@/components/JsonLdSchema";

import AdminGate from "./components/AdminGate";
import WhatsAppButton from "./components/WhatsAppButton";
import SEOMeta from "@/components/seo/SEOMeta";
import { StoreAdminSection } from "@/components/admin/StoreAdminSection";

const StoreDocsPage = React.lazy(() => import("@/pages/store/StoreDocsPage").then(m => ({ default: m.StoreDocsPage })));
const EcosystemDocsPage = React.lazy(() => import("@/pages/EcosystemDocsPage"));
const ArtifactDetailPage = React.lazy(() => import("./pages/ArtifactDetailPage"));
const AiSettingsPage = React.lazy(() => import("./pages/AiSettingsPage"));
const AiModelDocPage = React.lazy(() => import("./pages/AiModelDocPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const AirtimeCalculatorPage = React.lazy(() => import("@/pages/AirtimeCalculatorPage"));
const AirtimeComparisonPage = React.lazy(() => import("@/pages/AirtimeComparisonPage"));
const LivePricesPage = React.lazy(() => import("@/pages/LivePricesPage"));
const AirtimeToCashPage = React.lazy(() => import("@/pages/AirtimeToCashPage"));
const AiDocsPage = React.lazy(() => import("./pages/AiDocsPage"));
const McpDocsPage = React.lazy(() => import("./pages/McpDocsPage"));
const Landing = React.lazy(() => import("./pages/Landing"));
const VtuLandingPage = React.lazy(() => import("./pages/VtuLandingPage"));
const VtuDocsPage = React.lazy(() => import("./pages/VtuDocsPage"));
const Home = React.lazy(() => import("./pages/Home"));
const Index = React.lazy(() => import("./pages/Index"));
const Auth = React.lazy(() => import("./pages/Auth"));
const Transactions = React.lazy(() => import("./pages/Transactions"));
const WalletPage = React.lazy(() => import("./pages/WalletPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const BlogPage = React.lazy(() => import("./pages/BlogPage"));
const RewardsPage = React.lazy(() => import("./pages/RewardsPage"));
const DataPage = React.lazy(() => import("./pages/services/DataPage"));
const AirtimePage = React.lazy(() => import("./pages/services/AirtimePage"));
const CablePage = React.lazy(() => import("./pages/services/CablePage"));
const ElectricityPage = React.lazy(() => import("./pages/services/ElectricityPage"));
const ExamPinsPage = React.lazy(() => import("./pages/services/ExamPinsPage"));
const RechargeCardPage = React.lazy(() => import("./pages/services/RechargeCardPage"));
const SmsVerifyPage = React.lazy(() => import("./pages/services/SmsVerifyPage"));
const P2PTransferPage = React.lazy(() => import("./pages/P2PTransferPage"));
const MarketplacePage = React.lazy(() => import("./pages/MarketplacePage"));
const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const LegalPage = React.lazy(() => import("./pages/LegalPage"));
const TermsPage = React.lazy(() => import("./pages/legal/TermsPage"));
const PrivacyPage = React.lazy(() => import("./pages/legal/PrivacyPage"));
const RefundPage = React.lazy(() => import("./pages/legal/RefundPage"));
const CookiePage = React.lazy(() => import("./pages/legal/CookiePage"));
const KycPage = React.lazy(() => import("./pages/legal/KycPage"));
const ComplaintsPage = React.lazy(() => import("./pages/legal/ComplaintsPage"));
const DisclaimerPage = React.lazy(() => import("./pages/legal/DisclaimerPage"));
const CompliancePage = React.lazy(() => import("./pages/legal/CompliancePage"));
const SecurityPolicyPage = React.lazy(() => import("./pages/legal/SecurityPolicyPage"));
const DeveloperPage = React.lazy(() => import("./pages/DeveloperPage"));
const AmbassadorDashboard = React.lazy(() => import("./pages/AmbassadorDashboard"));
const VendorPanel = React.lazy(() => import("./pages/VendorPanel"));
const DnplPage = React.lazy(() => import("./pages/DnplPage"));
const LeaderboardPage = React.lazy(() => import("./pages/LeaderboardPage"));
const CampusSeoPage = React.lazy(() => import("./pages/CampusSeoPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const VoucherPage = React.lazy(() => import("./pages/VoucherPage"));
const WhatsAppFlyerPage = React.lazy(() => import("./pages/WhatsAppFlyerPage"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));
const SupportPage = React.lazy(() => import("./pages/SupportPage"));
const KycVerificationPage = React.lazy(() => import("./pages/KycVerificationPage"));
const BankDetailsPage = React.lazy(() => import("./pages/BankDetailsPage"));
const LivescorePage = React.lazy(() => import("./pages/LivescorePage"));
const DownloadPage = React.lazy(() => import("./pages/DownloadPage"));
const VendorSite = React.lazy(() => import("./pages/VendorSite"));
const PlayStorePage = React.lazy(() => import("./pages/PlayStorePage"));
const PressKitPage = React.lazy(() => import("./pages/PressKitPage"));
const EducationPillarPage = React.lazy(() => import("./pages/EducationPillarPage"));
const ColaCalculatorPage = React.lazy(() => import("./pages/tools/ColaCalculatorPage"));
const WeirdWebsitesPage = React.lazy(() => import("./pages/WeirdWebsitesPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const FAQPage = React.lazy(() => import("./pages/FAQPage"));
const RetailersPage = React.lazy(() => import("./pages/RetailersPage"));
const AmbassadorPage = React.lazy(() => import("./pages/AmbassadorPage"));
const RetailerDashboard = React.lazy(() => import("./pages/RetailerDashboard"));
const BlogPostPage = React.lazy(() => import("./pages/BlogPostPage"));
const NewsPage = React.lazy(() => import("./pages/NewsPage"));
const NewsStoryPage = React.lazy(() => import("./pages/NewsStoryPage"));
const WatchPage = React.lazy(() => import("./pages/WatchPage"));
const DataAnalyzer = React.lazy(() => import("./pages/DataAnalyzer"));
const EducationHubPage = React.lazy(() => import("./pages/EducationHubPage"));
const AiPage = React.lazy(() => import("./pages/AiPage"));
const AiAuthPage = React.lazy(() => import("./pages/AiAuthPage"));
const ComparePage = React.lazy(() => import("./pages/comparisons/ComparePage"));
const AiLandingPage = React.lazy(() => import("./pages/AiLandingPage"));
const AiPreviewPage = React.lazy(() => import("./pages/AiPreviewPage"));
const AiArticlesPage = React.lazy(() => import("./pages/AiArticlesPage"));
const AiConversationsPage = React.lazy(() => import("./pages/AiConversationsPage"));
const AiSubscriptionPage = React.lazy(() => import("./pages/AiSubscriptionPage"));
const Feed = React.lazy(() => import("./pages/Feed"));
const FeedPost = React.lazy(() => import("./pages/FeedPost"));
const SharePage = React.lazy(() => import("./pages/SharePage"));
const AiPricingPage = React.lazy(() => import("./pages/AiPricingPage"));
const AiUseCasesPage = React.lazy(() => import("./pages/AiUseCasesPage"));
const AiComparisonPage = React.lazy(() => import("./pages/AiComparisonPage"));
const CheapestVTUSitePage = React.lazy(() => import("./pages/CheapestVTUSitePage"));
const BestVTUPlatformPage = React.lazy(() => import("./pages/BestVTUPlatformPage"));
const HowToBuyCheapDataPage = React.lazy(() => import("./pages/HowToBuyCheapDataPage"));
const GuidesIndexPage = React.lazy(() => import("./pages/guides/GuidesIndexPage"));
const CheapestDataGuidePage = React.lazy(() => import("./pages/guides/CheapestDataGuidePage"));
const StoreHomePage = React.lazy(() => import("@/pages/store/StoreHomePage").then(m => ({ default: m.StoreHomePage })));
const ProductDetailPage = React.lazy(() => import("@/pages/store/ProductDetailPage").then(m => ({ default: m.ProductDetailPage })));
const CategoryPage = React.lazy(() => import("@/pages/store/CategoryPage").then(m => ({ default: m.CategoryPage })));
const BrandPage = React.lazy(() => import("@/pages/store/BrandPage").then(m => ({ default: m.BrandPage })));
const StoreSearchPage = React.lazy(() => import("@/pages/store/StoreSearchPage").then(m => ({ default: m.StoreSearchPage })));
const WholesalePage = React.lazy(() => import("@/pages/store/WholesalePage").then(m => ({ default: m.WholesalePage })));
const CartPage = React.lazy(() => import("@/pages/store/CartPage").then(m => ({ default: m.CartPage })));
const CheckoutPage = React.lazy(() => import("@/pages/store/CheckoutPage").then(m => ({ default: m.CheckoutPage })));
const StoreOrdersPage = React.lazy(() => import("@/pages/store/StoreOrdersPage").then(m => ({ default: m.StoreOrdersPage })));


const queryClient = new QueryClient();

// Resilient Error Boundary to eliminate blank screens
class PageErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Critical Page Error Caught:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background text-foreground text-center">
          <div className="w-14 h-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mb-4 border border-destructive/20 shadow-xs">
            <span className="text-2xl font-black">!</span>
          </div>
          <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md mb-4 leading-relaxed">
            An error occurred while displaying this section. Your session and account remain safe.
          </p>
          {this.state.error?.message && (
            <div className="mb-6 max-w-md w-full rounded-xl border border-border bg-card p-3 text-left">
              <p className="text-[11px] font-mono text-muted-foreground break-all">
                {this.state.error.message}
              </p>
            </div>
          )}
          <div className="flex flex-wrap gap-2.5 justify-center">
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-2 text-xs font-bold rounded-xl border border-border bg-card hover:bg-secondary transition-colors cursor-pointer"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-xs font-bold rounded-xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity cursor-pointer"
            >
              Reload Page
            </button>
            <a
              href="/"
              className="px-4 py-2 text-xs font-bold rounded-xl border border-border bg-secondary hover:bg-accent transition-colors"
            >
              Go to Homepage
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const isAiSubdomain = (() => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host.startsWith("ai.") || host === "ai.flamesnova.name.ng";
})();

const isCpSubdomain = (() => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host.startsWith("cp.") || host === "cp.flamesnova.name.ng";
})();

const isVendorSubdomain = (() => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const parts = host.split(".");
  return parts.length >= 4 && parts[parts.length - 3] === "flamesnova" && parts[0] !== "www";
})();

const isStoreSubdomain = (() => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host.startsWith("store.") || host === "store.flamesnova.name.ng";
})();

const StoreRoutes = () => (
  <div className="fn-atelier-store">
  <Routes>
    <Route path="/" element={<StoreHomePage />} />
    <Route path="/products/:slug" element={<ProductDetailPage />} />
    <Route path="/category/:slug" element={<CategoryPage />} />
    <Route path="/brand/:slug" element={<BrandPage />} />
    <Route path="/search" element={<StoreSearchPage />} />
    <Route path="/wholesale" element={<WholesalePage />} />
    <Route path="/cart" element={<CartPage />} />
    <Route path="/checkout" element={<CheckoutPage />} />
    <Route path="/orders" element={<StoreOrdersPage />} />
    <Route path="/docs" element={<StoreDocsPage />} />
    <Route path="/api" element={<StoreDocsPage />} />
    <Route path="/auth" element={<Auth />} />
    <Route path="/login" element={<Auth />} />
    <Route path="/signup" element={<Auth />} />
    <Route path="/register" element={<Auth />} />
    {/* Subdomain Legal Routes */}
    <Route path="/legal" element={<LegalPage />} />
    <Route path="/legal/terms" element={<TermsPage />} />
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/legal/privacy" element={<PrivacyPage />} />
    <Route path="/privacy" element={<PrivacyPage />} />
    <Route path="/legal/refund" element={<RefundPage />} />
    <Route path="/refund" element={<RefundPage />} />
    <Route path="/returns" element={<RefundPage />} />
    <Route path="/legal/cookie" element={<CookiePage />} />
    <Route path="/cookie" element={<CookiePage />} />
    <Route path="/legal/kyc" element={<KycPage />} />
    <Route path="/legal/complaints" element={<ComplaintsPage />} />
    <Route path="/legal/disclaimer" element={<DisclaimerPage />} />
    <Route path="/disclaimer" element={<DisclaimerPage />} />
    <Route path="/compliance" element={<CompliancePage />} />
    <Route path="/legal/compliance" element={<CompliancePage />} />
    <Route path="/security" element={<SecurityPolicyPage />} />
    <Route path="/legal/security" element={<SecurityPolicyPage />} />
    <Route path="*" element={<StoreHomePage />} />
  </Routes>
  </div>
);

const isVtuSubdomain = (() => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host.startsWith("vtu.") || host === "vtu.flamesnova.name.ng";
})();

const VtuHome = () => {
  const { session, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-xs font-medium text-muted-foreground">Loading Flames Nova VTU...</p>
        </div>
      </div>
    );
  }
  if (!session) {
    return <VtuLandingPage />;
  }
  return (
    <PageErrorBoundary>
      <Index />
    </PageErrorBoundary>
  );
};

const VtuRoutes = () => (
  <div className="flames-nova-vtu">
    <Routes>
      <Route path="/calculator" element={<AirtimeCalculatorPage />} />
      <Route path="/airtime-to-cash-calculator" element={<AirtimeCalculatorPage />} />
      <Route path="/compare-airtime-to-cash" element={<AirtimeComparisonPage />} />
      <Route path="/comparisons/airtime-to-cash" element={<AirtimeComparisonPage />} />
      <Route path="/live-prices" element={<LivePricesPage />} />
      <Route path="/prices" element={<LivePricesPage />} />
      <Route path="/status" element={<LivePricesPage />} />
          <Route path="/data-prices" element={<LivePricesPage />} />
          <Route path="/data-plans" element={<LivePricesPage />} />
      <Route path="/feed" element={<Navigate to="/blog" replace />} />
      <Route path="/dnpl" element={<Navigate to="/" replace />} />

      {/* ─── Public & Core Portal ─────────────────── */}
      <Route path="/" element={<VtuHome />} />
      <Route path="/docs" element={<VtuDocsPage />} />
            {/* VTU Gateway pathways on root domain */}
            <Route path="/vtu" element={<Home />} />
            <Route path="/vtu/dashboard" element={<RequireAuth><Index /></RequireAuth>} />
            <Route path="/vtu/wallet" element={<RequireAuth><WalletPage /></RequireAuth>} />
            <Route path="/vtu/transactions" element={<RequireAuth><Transactions /></RequireAuth>} />
      <Route path="/dashboard" element={<RequireAuth><Index /></RequireAuth>} />
      <Route path="/home" element={<RequireAuth><Index /></RequireAuth>} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/register" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<Navigate to="/auth" replace />} />
      <Route path="/airtime-to-cash" element={<AirtimeToCashPage />} />

      {/* ─── SEO Pages ────────────────────────────── */}
      <Route path="/cheapest-vtu-site-nigeria" element={<CheapestVTUSitePage />} />
      <Route path="/best-vtu-platform-nigeria" element={<BestVTUPlatformPage />} />
      <Route path="/how-to-buy-cheap-data-nigeria" element={<HowToBuyCheapDataPage />} />
      <Route path="/guides" element={<GuidesIndexPage />} />
      <Route path="/guides/cheapest-data-app-in-nigeria" element={<CheapestDataGuidePage />} />
      <Route path="/compare" element={<DataAnalyzer />} />
      <Route path="/compare/:slug" element={<ComparePage />} />

      {/* ─── Services (Auth Required) ─────────────── */}
      <Route path="/wallet" element={<RequireAuth><WalletPage /></RequireAuth>} />
      <Route path="/transactions" element={<RequireAuth><Transactions /></RequireAuth>} />
      <Route path="/services/data" element={<RequireAuth><DataPage /></RequireAuth>} />
      <Route path="/services/airtime" element={<RequireAuth><AirtimePage /></RequireAuth>} />
      <Route path="/services/cable" element={<RequireAuth><CablePage /></RequireAuth>} />
      <Route path="/services/electricity" element={<RequireAuth><ElectricityPage /></RequireAuth>} />
      <Route path="/services/exam-pins" element={<RequireAuth><ExamPinsPage /></RequireAuth>} />
      <Route path="/services/sms-verify" element={<RequireAuth><SmsVerifyPage /></RequireAuth>} />
      <Route path="/services/recharge-card" element={<RequireAuth><RechargeCardPage /></RequireAuth>} />
      <Route path="/services/virtual-number" element={<RequireAuth><SmsVerifyPage /></RequireAuth>} />
      <Route path="/services/airtime-to-cash" element={<AirtimeToCashPage />} />
      <Route path="/p2p-transfer" element={<RequireAuth><P2PTransferPage /></RequireAuth>} />
      <Route path="/p2p" element={<RequireAuth><P2PTransferPage /></RequireAuth>} />

      {/* ─── Account & Support ─────────────────────── */}
      <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
      <Route path="/rewards" element={<RequireAuth><RewardsPage /></RequireAuth>} />
      <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
      <Route path="/support" element={<RequireAuth><SupportPage /></RequireAuth>} />
      <Route path="/kyc" element={<RequireAuth><KycVerificationPage /></RequireAuth>} />
      <Route path="/bank-details" element={<RequireAuth><BankDetailsPage /></RequireAuth>} />
      <Route path="/education" element={<RequireAuth><EducationHubPage /></RequireAuth>} />
      <Route path="/share" element={<SharePage />} />

      {/* ─── Blog, AI & Ecosystem Pathways ────────── */}
      <Route path="/blog" element={<BlogPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:slug" element={<NewsStoryPage />} />
      <Route path="/blog/:slug" element={<BlogPage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/:slug" element={<NewsStoryPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/ai" element={<AiErrorBoundary fallbackTitle="Flames Nova AI Lite"><AiPage isStandalone={false} /></AiErrorBoundary>} />
      <Route path="/chat" element={<AiErrorBoundary fallbackTitle="Flames Nova AI Lite"><AiPage isStandalone={false} /></AiErrorBoundary>} />
      <Route path="/chat/:slug" element={<AiErrorBoundary fallbackTitle="Flames Nova AI Lite"><AiPage isStandalone={false} /></AiErrorBoundary>} />
      <Route path="/docs/ecosystem" element={<EcosystemDocsPage />} />
            {/* ─── Legal & Compliance Routes ─────────────────── */}
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/legal/refund" element={<RefundPage />} />
            <Route path="/legal/refunds" element={<RefundPage />} />
            <Route path="/refund" element={<RefundPage />} />
            <Route path="/refunds" element={<RefundPage />} />
            <Route path="/legal/cookie" element={<CookiePage />} />
            <Route path="/legal/cookies" element={<CookiePage />} />
            <Route path="/cookie" element={<CookiePage />} />
            <Route path="/cookies" element={<CookiePage />} />
            <Route path="/legal/kyc" element={<KycPage />} />
            <Route path="/kyc-policy" element={<KycPage />} />
            <Route path="/legal/complaints" element={<ComplaintsPage />} />
            <Route path="/complaints" element={<ComplaintsPage />} />
            <Route path="/legal/disclaimer" element={<DisclaimerPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/legal/compliance" element={<CompliancePage />} />
            <Route path="/security" element={<SecurityPolicyPage />} />
            <Route path="/legal/security" element={<SecurityPolicyPage />} />

      {/* ─── Admin Permanent Secret Relocation ────── */}
      <Route path="/cp-admin-8109795989" element={<AdminGate><AdminPage /></AdminGate>} />
      <Route path="/admin" element={<NotFound />} />
      <Route path="/admin/*" element={<NotFound />} />
      <Route path="/adminpanel292007" element={<NotFound />} />
      <Route path="/adminpanel292007/*" element={<NotFound />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
    <WhatsAppButton />
  </div>
);


const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
        <SEOMeta />
        <PageErrorBoundary>
        <React.Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}>
        {isStoreSubdomain ? (
          <StoreRoutes />
        ) : isVtuSubdomain ? (
          <VtuRoutes />
        ) : isCpSubdomain ? (
          <Routes>
          <Route path="/calculator" element={<AirtimeCalculatorPage />} />
          <Route path="/airtime-to-cash-calculator" element={<AirtimeCalculatorPage />} />
          <Route path="/compare-airtime-to-cash" element={<AirtimeComparisonPage />} />
          <Route path="/comparisons/airtime-to-cash" element={<AirtimeComparisonPage />} />
          <Route path="/live-prices" element={<LivePricesPage />} />
          <Route path="/prices" element={<LivePricesPage />} />
          <Route path="/status" element={<LivePricesPage />} />
          <Route path="/data-prices" element={<LivePricesPage />} />
          <Route path="/data-plans" element={<LivePricesPage />} />
          <Route path="/feed" element={<Navigate to="/blog" replace />} />
          <Route path="/dnpl" element={<Navigate to="/" replace />} />

            <Route path="/cp-admin-8109795989" element={<AdminGate><AdminPage /></AdminGate>} />
            <Route path="/admin" element={<NotFound />} />
            <Route path="/admin/*" element={<NotFound />} />
            <Route path="/adminpanel292007" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        ) : isAiSubdomain ? (
          <Routes>
          <Route path="/calculator" element={<AirtimeCalculatorPage />} />
          <Route path="/airtime-to-cash-calculator" element={<AirtimeCalculatorPage />} />
          <Route path="/compare-airtime-to-cash" element={<AirtimeComparisonPage />} />
          <Route path="/comparisons/airtime-to-cash" element={<AirtimeComparisonPage />} />
          <Route path="/live-prices" element={<LivePricesPage />} />
          <Route path="/prices" element={<LivePricesPage />} />
          <Route path="/status" element={<LivePricesPage />} />
          <Route path="/data-prices" element={<LivePricesPage />} />
          <Route path="/data-plans" element={<LivePricesPage />} />
          <Route path="/feed" element={<Navigate to="/blog" replace />} />
          <Route path="/dnpl" element={<Navigate to="/" replace />} />

            <Route path="/" element={<AiLandingPage />} />
            <Route path="/auth" element={<AiAuthPage />} />
            <Route path="/ai/chat" element={<Navigate to="/chat" replace />} />
            <Route path="/ai/chat" element={<Navigate to="/chat" replace />} />
            <Route path="/chat" element={<AiPage isStandalone={true} />} />
            <Route path="/chat/:slug" element={<AiPage isStandalone={true} />} />
            <Route path="/ai/chat/:slug" element={<AiPage isStandalone={true} />} />
            <Route path="/artifacts/:id" element={<ArtifactDetailPage />} />
            <Route path="/ai/artifacts/:id" element={<ArtifactDetailPage />} />
            <Route path="/artifact/:id" element={<ArtifactDetailPage />} />
            <Route path="/ai/settings" element={<AiErrorBoundary><AiSettingsPage /></AiErrorBoundary>} />
            <Route path="/settings" element={<AiErrorBoundary><AiSettingsPage /></AiErrorBoundary>} />
            <Route path="/ai" element={<AiErrorBoundary><AiPage isStandalone={true} /></AiErrorBoundary>} />
            <Route path="/models" element={<AiModelDocPage />} />
            <Route path="/models/:modelId" element={<AiModelDocPage />} />
            <Route path="/ai/models/:modelId" element={<AiModelDocPage />} />

            <Route path="/pricing" element={<AiPricingPage />} />
            <Route path="/ai/pricing" element={<AiPricingPage />} />
            <Route path="/use-cases" element={<AiUseCasesPage />} />
            <Route path="/ai/use-cases" element={<AiUseCasesPage />} />
            <Route path="/comparison" element={<AiComparisonPage />} />
            <Route path="/ai/comparison" element={<AiComparisonPage />} />
            <Route path="/docs" element={<AiDocsPage />} />
            <Route path="/docs/ecosystem" element={<EcosystemDocsPage />} />
        <Route path="/docs/mcp" element={<McpDocsPage />} />
        <Route path="/mcp" element={<McpDocsPage />} />
        <Route path="/mcp/docs" element={<McpDocsPage />} />
            <Route path="/ai/docs" element={<AiDocsPage />} />
            <Route path="/articles" element={<AiArticlesPage />} />
            <Route path="/ai/articles" element={<AiArticlesPage />} />
            <Route path="/preview/:slug" element={<AiPreviewPage />} />
            <Route path="/artifacts/:id" element={<ArtifactDetailPage />} />
            <Route path="/ai/artifacts/:id" element={<ArtifactDetailPage />} />
            <Route path="/artifact/:id" element={<ArtifactDetailPage />} />

            <Route path="/ai/preview/:slug" element={<AiPreviewPage />} />
            <Route path="/AI/:slug" element={<AiPreviewPage />} />
            <Route path="/conversations" element={<RequireAuth><AiConversationsPage /></RequireAuth>} />
            <Route path="/ai/conversations" element={<RequireAuth><AiConversationsPage /></RequireAuth>} />
            <Route path="/subscription" element={<RequireAuth><AiSubscriptionPage /></RequireAuth>} />
            <Route path="/ai/subscription" element={<RequireAuth><AiSubscriptionPage /></RequireAuth>} />
            <Route path="/airtime-to-cash" element={<AirtimeToCashPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/popular" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
            {/* ─── Legal & Compliance Routes ─────────────────── */}
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/legal/refund" element={<RefundPage />} />
            <Route path="/legal/refunds" element={<RefundPage />} />
            <Route path="/refund" element={<RefundPage />} />
            <Route path="/refunds" element={<RefundPage />} />
            <Route path="/legal/cookie" element={<CookiePage />} />
            <Route path="/legal/cookies" element={<CookiePage />} />
            <Route path="/cookie" element={<CookiePage />} />
            <Route path="/cookies" element={<CookiePage />} />
            <Route path="/legal/kyc" element={<KycPage />} />
            <Route path="/kyc-policy" element={<KycPage />} />
            <Route path="/legal/complaints" element={<ComplaintsPage />} />
            <Route path="/complaints" element={<ComplaintsPage />} />
            <Route path="/legal/disclaimer" element={<DisclaimerPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/legal/compliance" element={<CompliancePage />} />
            <Route path="/security" element={<SecurityPolicyPage />} />
            <Route path="/legal/security" element={<SecurityPolicyPage />} />
            <Route path="/admin" element={<NotFound />} />
            <Route path="/admin/*" element={<NotFound />} />
            <Route path="/adminpanel292007" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        ) : isVendorSubdomain ? (
          <Routes>
          <Route path="/calculator" element={<AirtimeCalculatorPage />} />
          <Route path="/airtime-to-cash-calculator" element={<AirtimeCalculatorPage />} />
          <Route path="/compare-airtime-to-cash" element={<AirtimeComparisonPage />} />
          <Route path="/comparisons/airtime-to-cash" element={<AirtimeComparisonPage />} />
          <Route path="/live-prices" element={<LivePricesPage />} />
          <Route path="/prices" element={<LivePricesPage />} />
          <Route path="/status" element={<LivePricesPage />} />
          <Route path="/data-prices" element={<LivePricesPage />} />
          <Route path="/data-plans" element={<LivePricesPage />} />
          <Route path="/feed" element={<Navigate to="/blog" replace />} />
          <Route path="/dnpl" element={<Navigate to="/" replace />} />

            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<Navigate to="/auth" replace />} />
            <Route path="/wallet" element={<RequireAuth><WalletPage /></RequireAuth>} />
            <Route path="/services/data" element={<RequireAuth><DataPage /></RequireAuth>} />
            <Route path="/services/airtime" element={<RequireAuth><AirtimePage /></RequireAuth>} />
            <Route path="/services/cable" element={<RequireAuth><CablePage /></RequireAuth>} />
            <Route path="/services/electricity" element={<RequireAuth><ElectricityPage /></RequireAuth>} />
            <Route path="/services/exam-pins" element={<RequireAuth><ExamPinsPage /></RequireAuth>} />
            <Route path="/services/recharge-card" element={<RequireAuth><RechargeCardPage /></RequireAuth>} />
            <Route path="/services/virtual-number" element={<RequireAuth><SmsVerifyPage /></RequireAuth>} />
            <Route path="/services/sms-verify" element={<RequireAuth><SmsVerifyPage /></RequireAuth>} />
            <Route path="/transactions" element={<RequireAuth><Transactions /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
            <Route path="*" element={<VendorSite />} />
          </Routes>
        ) : (
        <>
          <JsonLdSchema />
          <Routes>
          <Route path="/calculator" element={<AirtimeCalculatorPage />} />
          <Route path="/airtime-to-cash-calculator" element={<AirtimeCalculatorPage />} />
          <Route path="/compare-airtime-to-cash" element={<AirtimeComparisonPage />} />
          <Route path="/comparisons/airtime-to-cash" element={<AirtimeComparisonPage />} />
          <Route path="/live-prices" element={<LivePricesPage />} />
          <Route path="/prices" element={<LivePricesPage />} />
          <Route path="/status" element={<LivePricesPage />} />
          <Route path="/data-prices" element={<LivePricesPage />} />
          <Route path="/data-plans" element={<LivePricesPage />} />
          <Route path="/feed" element={<Navigate to="/blog" replace />} />
          <Route path="/dnpl" element={<Navigate to="/" replace />} />

            {/* ─── Public Pages (No Auth Required) ─────────────────── */}
            <Route path="/" element={<Landing />} />
            <Route path="/docs" element={<EcosystemDocsPage />} />
            <Route path="/docs/:slug" element={<EcosystemDocsPage />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<Navigate to="/auth" replace />} />
            <Route path="/airtime-to-cash" element={<AirtimeToCashPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/how-to-get-help-in-windows-11-10-20-easy-methods-2026-2027" element={<Navigate to="/blog/how-to-get-help-in-windows" replace />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/articles/:slug" element={<BlogPostPage />} />
            <Route path="/article/:slug" element={<BlogPostPage />} />
            {/* ─── Flames Nova News Discovery & Briefings ─────────────────── */}
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/technology" element={<NewsPage />} />
            <Route path="/news/business" element={<NewsPage />} />
            <Route path="/news/world" element={<NewsPage />} />
            <Route path="/news/ai" element={<NewsPage />} />
            <Route path="/news/africa" element={<NewsPage />} />
            <Route path="/news/entertainment" element={<NewsPage />} />
            <Route path="/news/sports" element={<NewsPage />} />
            <Route path="/news/science" element={<NewsPage />} />
            <Route path="/news/climate" element={<NewsPage />} />
            <Route path="/news/category/:category" element={<NewsPage />} />
            <Route path="/news/topic/:topic" element={<NewsPage />} />
            <Route path="/news/:slug" element={<NewsStoryPage />} />
            <Route path="/watch" element={<WatchPage />} />
            <Route path="/watch/:slug" element={<WatchPage />} />
            <Route path="/feed" element={<Feed />} />
            <Route path="/feed/:slug" element={<FeedPost />} />
            <Route path="/faq" element={<FAQPage />} />
            {/* ─── Legal & Compliance Routes ─────────────────── */}
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/legal/refund" element={<RefundPage />} />
            <Route path="/legal/refunds" element={<RefundPage />} />
            <Route path="/refund" element={<RefundPage />} />
            <Route path="/refunds" element={<RefundPage />} />
            <Route path="/legal/cookie" element={<CookiePage />} />
            <Route path="/legal/cookies" element={<CookiePage />} />
            <Route path="/cookie" element={<CookiePage />} />
            <Route path="/cookies" element={<CookiePage />} />
            <Route path="/legal/kyc" element={<KycPage />} />
            <Route path="/kyc-policy" element={<KycPage />} />
            <Route path="/legal/complaints" element={<ComplaintsPage />} />
            <Route path="/complaints" element={<ComplaintsPage />} />
            <Route path="/legal/disclaimer" element={<DisclaimerPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/legal/compliance" element={<CompliancePage />} />
            <Route path="/security" element={<SecurityPolicyPage />} />
            <Route path="/legal/security" element={<SecurityPolicyPage />} />
            <Route path="/livescore" element={<LivescorePage />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/play-store" element={<PlayStorePage />} />
            <Route path="/press-kit" element={<PressKitPage />} />
            <Route path="/tools/social-security-2027-cola-calculator" element={<ColaCalculatorPage />} />
            <Route path="/weird-websites/" element={<WeirdWebsitesPage />} />
            <Route path="/weird-websites" element={<WeirdWebsitesPage />} />
            <Route path="/waec-neco-jamb-result-checker" element={<EducationPillarPage />} />
            <Route path="/compare" element={<DataAnalyzer />} />
            <Route path="/compare/:slug" element={<ComparePage />} />

            {/* ─── AI Pages (Public — No Auth Required) ────────────── */}
            <Route path="/docs/mcp" element={<McpDocsPage />} />
        <Route path="/mcp" element={<McpDocsPage />} />
        <Route path="/mcp/docs" element={<McpDocsPage />} />
            <Route path="/ai/docs" element={<McpDocsPage />} />
            <Route path="/chat" element={<AiPage isStandalone={true} />} />
            <Route path="/chat/:slug" element={<AiPage isStandalone={true} />} />
            <Route path="/chat/:slug" element={<AiPage isStandalone={true} />} />
            <Route path="/ai/chat/:slug" element={<AiPage isStandalone={true} />} />
            <Route path="/ai" element={<AiPage isStandalone={false} />} />
            <Route path="/ai-landing" element={<AiLandingPage />} />
            <Route path="/ai/preview/:slug" element={<AiPreviewPage />} />
            <Route path="/ai/articles" element={<AiArticlesPage />} />
            <Route path="/ai/pricing" element={<AiPricingPage />} />
            <Route path="/ai/use-cases" element={<AiUseCasesPage />} />
            <Route path="/ai/comparison" element={<AiComparisonPage />} />

            {/* ─── SEO Pages (Public — No Auth Required) ───────────── */}
            <Route path="/cheapest-vtu-site-nigeria" element={<CheapestVTUSitePage />} />
            <Route path="/best-vtu-platform-nigeria" element={<BestVTUPlatformPage />} />
            <Route path="/how-to-buy-cheap-data-nigeria" element={<HowToBuyCheapDataPage />} />
            <Route path="/guides" element={<GuidesIndexPage />} />
            <Route path="/guides/cheapest-data-app-in-nigeria" element={<CheapestDataGuidePage />} />

            {/* ─── Campus (Public) ──────────────────────────────────── */}
            <Route path="/campus" element={<CampusSeoPage />} />
            <Route path="/campus/:slug" element={<CampusSeoPage />} />
            <Route path="/s/:subdomain" element={<VendorSite />} />

            {/* ─── Auth-Required Pages ──────────────────────────────── */}
            <Route path="/home" element={<RequireAuth><Index /></RequireAuth>} />
            <Route path="/transactions" element={<RequireAuth><Transactions /></RequireAuth>} />
            <Route path="/wallet" element={<RequireAuth><WalletPage /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
            <Route path="/rewards" element={<RequireAuth><RewardsPage /></RequireAuth>} />
            <Route path="/services/data" element={<RequireAuth><DataPage /></RequireAuth>} />
            <Route path="/services/airtime" element={<RequireAuth><AirtimePage /></RequireAuth>} />
            <Route path="/services/cable" element={<RequireAuth><CablePage /></RequireAuth>} />
            <Route path="/services/electricity" element={<RequireAuth><ElectricityPage /></RequireAuth>} />
            <Route path="/services/exam-pins" element={<RequireAuth><ExamPinsPage /></RequireAuth>} />
            <Route path="/services/sms-verify" element={<RequireAuth><SmsVerifyPage /></RequireAuth>} />
            <Route path="/services/recharge-card" element={<RequireAuth><RechargeCardPage /></RequireAuth>} />
            <Route path="/services/virtual-number" element={<RequireAuth><SmsVerifyPage /></RequireAuth>} />
            <Route path="/services/airtime-to-cash" element={<AirtimeToCashPage />} />
            <Route path="/p2p-transfer" element={<RequireAuth><P2PTransferPage /></RequireAuth>} />
            <Route path="/marketplace" element={<Navigate to="/airtime-to-cash" replace />} />
            
            {/* ─── Admin Permanent Secret Relocation ────────────────── */}
            <Route path="/cp-admin-8109795989" element={<AdminGate><AdminPage /></AdminGate>} />
            <Route path="/admin" element={<NotFound />} />
            <Route path="/admin/*" element={<NotFound />} />
            <Route path="/adminpanel292007" element={<NotFound />} />
            <Route path="/adminpanel292007/*" element={<NotFound />} />

            <Route path="/developer" element={<RequireAuth><DeveloperPage /></RequireAuth>} />
            <Route path="/ambassador" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/ambassador/dashboard" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/vendor-panel" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/retailers" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/retailer-dashboard" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/dnpl" element={<RequireAuth><DnplPage /></RequireAuth>} />
            <Route path="/leaderboard" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/voucher" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/flyer" element={<Navigate to="/airtime-to-cash" replace />} />
            <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
            <Route path="/support" element={<RequireAuth><SupportPage /></RequireAuth>} />
            <Route path="/kyc" element={<RequireAuth><KycVerificationPage /></RequireAuth>} />
            <Route path="/bank-details" element={<RequireAuth><BankDetailsPage /></RequireAuth>} />
            <Route path="/education" element={<RequireAuth><EducationHubPage /></RequireAuth>} />
            <Route path="/ai/conversations" element={<RequireAuth><AiConversationsPage /></RequireAuth>} />
            <Route path="/ai/subscription" element={<RequireAuth><AiSubscriptionPage /></RequireAuth>} />
            <Route path="/share" element={<SharePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <WhatsAppButton />
        </>
        )}
        </React.Suspense>
        </PageErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
